import { useCallback } from "preact/hooks";
import { useData } from "../hooks/useData";
import { useTimeRange, type TimeRange } from "../hooks/useTimeRange";
import { fetchHardware, fetchHardwareHistory } from "../utils/api";
import { StatusBadge } from "../components/StatusBadge";
import { TimeRangePicker } from "../components/TimeRangePicker";
import { SkeletonLoader } from "../components/SkeletonLoader";
import { ErrorMessage } from "../components/ErrorMessage";
import { LastUpdated } from "../components/LastUpdated";
import { ResourceChart } from "../components/charts/ResourceChart";
import { SignalChart } from "../components/charts/SignalChart";
import "./Hardware.css";

const DEVICE_RB = "RB750Gr3";
const DEVICE_LHG = "LHG";

function DeviceSection({
  title,
  type,
  device,
  loading,
  children,
}: {
  title: string;
  type: string;
  device: { connection_status: "up" | "down"; last_update: string } | undefined;
  loading: boolean;
  children: preact.ComponentChildren;
}) {
  return (
    <section class="hardware__device card">
      <div class="hardware__device-header">
        <div>
          <h2 class="hardware__device-title">{title}</h2>
          <span class="hardware__device-type">{type}</span>
        </div>
        {loading || !device ? (
          <SkeletonLoader type="card" />
        ) : (
          <div class="hardware__device-meta">
            <StatusBadge status={device.connection_status} />
            <LastUpdated timestamp={device.last_update} />
          </div>
        )}
      </div>
      {children}
    </section>
  );
}

export default function Hardware() {
  const { range: routerRange, setRange: setRouterRange } = useTimeRange();
  const { range: wirelessRange, setRange: setWirelessRange } = useTimeRange();

  const fetchHardwareCb = useCallback(() => fetchHardware(), []);
  const {
    data: hardware,
    loading: hardwareLoading,
    error: hardwareError,
    refresh: refreshHardware,
  } = useData(fetchHardwareCb);

  const fetchRouterHistoryCb = useCallback(
    () => fetchHardwareHistory(DEVICE_RB, routerRange),
    [routerRange]
  );
  const {
    data: routerHistory,
    loading: routerHistoryLoading,
    error: routerHistoryError,
    refresh: refreshRouterHistory,
  } = useData(fetchRouterHistoryCb);

  const fetchLhgHistoryCb = useCallback(
    () => fetchHardwareHistory(DEVICE_LHG, wirelessRange),
    [wirelessRange]
  );
  const {
    data: lhgHistory,
    loading: lhgHistoryLoading,
    error: lhgHistoryError,
    refresh: refreshLhgHistory,
  } = useData(fetchLhgHistoryCb);

  const handleRouterRange = (r: string) => setRouterRange(r as TimeRange);
  const handleWirelessRange = (r: string) => setWirelessRange(r as TimeRange);

  const rbDevice = hardware?.devices.find((d) => d.name === DEVICE_RB);
  const lhgDevice = hardware?.devices.find((d) => d.name === DEVICE_LHG);

  return (
    <div class="hardware">
      <h1 class="hardware__title">Hardware Monitoring</h1>

      {hardwareError && (
        <ErrorMessage message={hardwareError.message} onRetry={refreshHardware} />
      )}

      <DeviceSection
        title={DEVICE_RB}
        type="Router"
        device={rbDevice}
        loading={hardwareLoading}
      >
        <div class="hardware__section">
          <div class="hardware__section-header">
            <h3 class="hardware__section-title">CPU & Memory</h3>
            <TimeRangePicker value={routerRange} onChange={handleRouterRange} />
          </div>
          {routerHistoryError && (
            <ErrorMessage
              message={routerHistoryError.message}
              onRetry={refreshRouterHistory}
            />
          )}
          <ResourceChart
            data={routerHistory?.resource_history ?? []}
            loading={routerHistoryLoading}
            error={routerHistoryError?.message}
          />
        </div>

        <div class="hardware__section">
          <h3 class="hardware__section-title">Interface Status</h3>
          {hardwareLoading ? (
            <SkeletonLoader type="card" />
          ) : rbDevice?.interfaces.length ? (
            <div class="hardware__interface-grid">
              {rbDevice.interfaces.map((iface) => (
                <div key={iface.name} class="hardware__interface-card">
                  <span class="hardware__interface-name">{iface.name}</span>
                  <StatusBadge
                    status={
                      iface.status === "up" || iface.status === "down"
                        ? iface.status
                        : "unknown"
                    }
                  />
                </div>
              ))}
            </div>
          ) : (
            <p class="hardware__empty">Belum ada data interface</p>
          )}
        </div>
      </DeviceSection>

      <DeviceSection
        title={DEVICE_LHG}
        type="Wireless"
        device={lhgDevice}
        loading={hardwareLoading}
      >
        <div class="hardware__section">
          <div class="hardware__section-header">
            <h3 class="hardware__section-title">CPU & Memory</h3>
            <TimeRangePicker
              value={wirelessRange}
              onChange={handleWirelessRange}
            />
          </div>
          {lhgHistoryError && (
            <ErrorMessage
              message={lhgHistoryError.message}
              onRetry={refreshLhgHistory}
            />
          )}
          <ResourceChart
            data={lhgHistory?.resource_history ?? []}
            loading={lhgHistoryLoading}
            error={lhgHistoryError?.message}
          />
        </div>

        <div class="hardware__section">
          <h3 class="hardware__section-title">Interface Status</h3>
          {hardwareLoading ? (
            <SkeletonLoader type="card" />
          ) : lhgDevice?.interfaces.length ? (
            <div class="hardware__interface-grid">
              {lhgDevice.interfaces.map((iface) => (
                <div key={iface.name} class="hardware__interface-card">
                  <span class="hardware__interface-name">{iface.name}</span>
                  <StatusBadge
                    status={
                      iface.status === "up" || iface.status === "down"
                        ? iface.status
                        : "unknown"
                    }
                  />
                </div>
              ))}
            </div>
          ) : (
            <p class="hardware__empty">Belum ada data interface</p>
          )}
        </div>

        <div class="hardware__section">
          <h3 class="hardware__section-title">Signal Antenna</h3>
          <SignalChart
            data={lhgHistory?.wireless_history ?? []}
            loading={lhgHistoryLoading}
            error={lhgHistoryError?.message}
          />
        </div>
      </DeviceSection>
    </div>
  );
}
